// Association portal, slice 5: error mapping for the advisor roster
// (`POST /association/{id}/advisor` and
// `DELETE /association/{id}/advisor/{advisorProfileId}`).
//
// The backend answers roster failures with `400 {errorCode, message}`
// (AssociationErrorCode, never 401/403). Bean validation of the `{email}` body
// answers a flat `400 {field: message}` map without an errorCode; the form
// checks the e-mail shape first, so that path is unreachable in practice.
//
// CONTRACT FACT, not a bandaid: the backend reports both "no apoteka.rs user
// with this e-mail" and "the user has no advisor profile yet" as
// `ASSOCIATION_INVALID_INPUT`, and only the message text tells them apart
// ("No apoteka.rs user ..." vs "... has not created an advisor profile yet.").
// The backend dev will give them their own codes; replace the two text
// matches below with code checks when that lands.

type RejectionLike = {
  response?: {
    status?: number;
    data?: unknown;
  };
};

type ErrorBody = { errorCode?: unknown; message?: unknown } & Record<
  string,
  unknown
>;

const body = (error: unknown): ErrorBody | undefined => {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }
  const data = (error as RejectionLike).response?.data;
  return typeof data === "object" && data !== null
    ? (data as ErrorBody)
    : undefined;
};

const status = (error: unknown): number | undefined =>
  typeof error === "object" && error !== null
    ? (error as RejectionLike).response?.status
    : undefined;

const errorCode = (error: unknown): string | undefined => {
  const code = body(error)?.errorCode;
  return typeof code === "string" ? code : undefined;
};

const backendMessage = (error: unknown): string | undefined => {
  const message = body(error)?.message;
  return typeof message === "string" && message.trim() ? message : undefined;
};

/**
 * The first message of a bean-validation `{field: message}` map (a 400
 * without an errorCode; the key is `addAdvisorToAssociationRequest.email`).
 */
const firstValidationMessage = (error: unknown): string | undefined => {
  const data = body(error);
  if (status(error) !== 400 || !data || data.errorCode !== undefined) {
    return undefined;
  }
  const first = Object.values(data).find(
    (value) => typeof value === "string" && value.trim(),
  );
  return typeof first === "string" ? first : undefined;
};

export const ROSTER_ROLE_REQUIRED_COPY =
  "Ovaj nalog nema ulogu savetnika. Ulogu dodeljuje administrator.";
export const ROSTER_ALREADY_MEMBER_COPY = "Savetnik je već član ovog udruženja.";
export const ROSTER_NO_ACCOUNT_COPY =
  "Ne postoji apoteka.rs nalog sa ovom e-mail adresom.";
export const ROSTER_NO_PROFILE_COPY =
  "Savetnik još nije kreirao svoj profil na dobrocudan.rs.";
export const ROSTER_INVALID_INPUT_COPY = "Podaci nisu prihvaćeni.";
export const ROSTER_NOT_ADMINISTERED_COPY =
  "Nemate pravo da upravljate ovim udruženjem.";
export const ROSTER_SAVE_FAILED_COPY = "Čuvanje nije uspelo. Pokušajte ponovo.";
export const ROSTER_REMOVE_FAILED_COPY =
  "Uklanjanje nije uspelo. Pokušajte ponovo.";

/** Message-text markers of the two `ASSOCIATION_INVALID_INPUT` roster cases. */
const NO_ACCOUNT_MARKER = "No apoteka.rs user";
const NO_PROFILE_MARKER = "has not created an advisor profile";

/** Backend `AssociationErrorCode.ASSOCIATION_NOT_ADMINISTERED`, always a 400. */
export const isAssociationNotAdministered = (error: unknown): boolean =>
  errorCode(error) === "ASSOCIATION_NOT_ADMINISTERED";

/** Backend `AssociationErrorCode.ADVISOR_NOT_IN_ASSOCIATION`, always a 400. */
export const isAdvisorNotInAssociation = (error: unknown): boolean =>
  errorCode(error) === "ADVISOR_NOT_IN_ASSOCIATION";

/**
 * The advisor profile id no longer exists (deleted profile). The backend
 * answers `404` with a plain-text body, outside the errorCode contract.
 */
export const isAdvisorProfileGone = (error: unknown): boolean =>
  status(error) === 404;

/** `POST /association/{id}/advisor` failure -> the copy shown under the input. */
export const mapAddAdvisorError = (error: unknown): string => {
  switch (errorCode(error)) {
    case "ADVISOR_ROLE_REQUIRED":
      return ROSTER_ROLE_REQUIRED_COPY;
    case "ADVISOR_ALREADY_IN_ASSOCIATION":
      return ROSTER_ALREADY_MEMBER_COPY;
    case "ASSOCIATION_NOT_ADMINISTERED":
      return ROSTER_NOT_ADMINISTERED_COPY;
    case "ASSOCIATION_INVALID_INPUT": {
      const message = backendMessage(error);
      if (message?.includes(NO_ACCOUNT_MARKER)) {
        return ROSTER_NO_ACCOUNT_COPY;
      }
      if (message?.includes(NO_PROFILE_MARKER)) {
        return ROSTER_NO_PROFILE_COPY;
      }
      return message ?? ROSTER_INVALID_INPUT_COPY;
    }
    default:
      return firstValidationMessage(error) ?? ROSTER_SAVE_FAILED_COPY;
  }
};

/**
 * `DELETE /association/{id}/advisor/{advisorProfileId}` failure -> the copy
 * shown in the confirm dialog. `ADVISOR_NOT_IN_ASSOCIATION` and the 404 are
 * not mapped here: the page answers both by re-reading the roster.
 */
export const mapRemoveAdvisorError = (error: unknown): string =>
  isAssociationNotAdministered(error)
    ? ROSTER_NOT_ADMINISTERED_COPY
    : ROSTER_REMOVE_FAILED_COPY;
